// 答案送出格式檢核（規則文件：src/utils/answerFormat.md）
// 後端不擋題型/格式，交卷前在前端檢核；fail-open：只回傳違規清單，
// 不丟例外、不擋送出，由呼叫端決定通報方式（目前打 Discord）。

const SINGLE_CHOICE_TYPES = ['radiogroup', 'dropdown']
const OPEN_TYPES = ['comment', 'text', 'file', 'signaturepad']

function isInt(v) {
  return Number.isInteger(v)
}

function hasDuplicates(arr) {
  return new Set(arr).size !== arr.length
}

// 檢核單題的選擇題索引：整數、範圍內、（複選/排序）不重複
function checkChoiceIndexes(tag, ans, bound, allowDuplicates, violations, specialIndexes = []) {
  ans.forEach((v) => {
    if (!isInt(v)) {
      violations.push(`${tag}: ans 含非整數索引 ${JSON.stringify(v)}`)
    } else if (bound > 0 && (v < 0 || v >= bound) && !specialIndexes.includes(v)) {
      violations.push(
        `${tag}: ans 索引 ${v} 超出選項範圍（共 ${bound} 個選項，應為 0～${
          bound - 1
        }）`
      )
    }
  })
  if (!allowDuplicates && hasDuplicates(ans)) {
    violations.push(`${tag}: ans 索引重複 ${JSON.stringify(ans)}`)
  }
}

/**
 * 檢核交卷 payload 的每題答案格式
 * @param {object} survey SurveyJS model（handleComplete 的 sender）
 * @param {Array} answers 排序後的 answers 陣列（每題 { pi, qi, ans, ord, other, ct, bringing_options? }）
 * @param {string} pid 專案編號
 * @returns {string[]} 違規清單（空陣列 = 合規）
 */
export function validateAnswerPayload(survey, answers, pid) {
  const violations = []
  if (!Array.isArray(answers)) {
    return ['answers 不是陣列']
  }

  answers.forEach((item) => {
    if (!item || typeof item !== 'object') {
      violations.push(`answers 含非物件項目 ${JSON.stringify(item)}`)
      return
    }
    // 題號用 Agree.js 的寫法（qi + 1），括號附 payload 原始 qi 方便對 Network
    const tag = isInt(item.qi)
      ? `q${item.qi + 1}(qi=${item.qi})`
      : `q${item.qi}`

    // dkabio（奇美）在送出前另外 push 一筆 qi:0 的假題塞報告連結，
    // 題名是從 q0 開始編的，所以 payload 裡會有「真的 q0」與「假的 q0」兩筆，
    // 只能靠 ans 是字串連結來認出假的那筆，不能整個 qi:0 跳掉。
    if (
      pid === 'P240719175039533' &&
      item.qi === 0 &&
      typeof item.ans?.[0] === 'string'
    ) {
      return
    }

    // 必要欄位與 null 檢查
    if (typeof item.pi !== 'string' || item.pi === '') {
      violations.push(`${tag}: pi 缺少或不是字串`)
    } else if (pid && item.pi !== pid) {
      violations.push(`${tag}: pi (${item.pi}) 與 pid (${pid}) 不符`)
    }
    if (!isInt(item.qi)) {
      violations.push(`${tag}: qi 缺少或不是整數`)
    }
    if (!Array.isArray(item.ans)) {
      violations.push(`${tag}: ans 缺少或不是陣列`)
      return
    }
    if (!Array.isArray(item.ord)) {
      violations.push(`${tag}: ord 缺少或不是陣列`)
    }
    if (item.ans.some((v) => v === null || v === undefined)) {
      violations.push(`${tag}: ans 含 null/undefined`)
    }
    if (
      item.other != null &&
      (typeof item.other !== 'object' || Array.isArray(item.other))
    ) {
      violations.push(`${tag}: other 不是物件`)
    }

    // 特例注入題（如 dkabio qi:0）或找不到題目定義：不做題型檢核
    const question = survey?.getQuestionByName?.(`q${item.qi}`)
    if (!question) {
      return
    }

    const type = question.getType()
    const ordLen = Array.isArray(item.ord) ? item.ord.length : 0
    const choiceCount = Array.isArray(question.visibleChoices)
      ? question.visibleChoices.length
      : 0
    // ans 索引是「顯示順序」索引，以 ord 長度為界（退回 visibleChoices 數）
    const bound = ordLen || choiceCount
    const isMultiple = type === 'checkbox' || (type === 'imagepicker' && question.multiSelect)
    const otherIndex = question.showOtherItem && question.choices
      ? question.choices.length + (question.showNoneItem ? 1 : 0)
      : -1
    const noneIndex = question.showNoneItem && question.choices
      ? question.choices.length
      : -1
    // 特殊選項採完整 choices 索引時，可能超過可見 ord 的長度。
    const specialIndexes = (isMultiple ? [] : [otherIndex, noneIndex])
      .filter((index) => index >= 0)

    // 題型 ↔ ans 形狀（answerFormat.md 第 2 節）
    if (
      SINGLE_CHOICE_TYPES.includes(type) ||
      (type === 'imagepicker' && !question.multiSelect)
    ) {
      // 單選/下拉：長度 1（地址題目前尚無，暫按單選規則）
      if (item.ans.length !== 1) {
        violations.push(`${tag}: 單選題 ans 長度應為 1，實際 ${item.ans.length}`)
      }
      checkChoiceIndexes(tag, item.ans, bound, false, violations, specialIndexes)
    } else if (
      type === 'checkbox' ||
      (type === 'imagepicker' && question.multiSelect)
    ) {
      if (item.ans.length === 0) {
        violations.push(`${tag}: 複選題 ans 為空陣列`)
      }
      checkChoiceIndexes(tag, item.ans, bound, false, violations, specialIndexes)
    } else if (type === 'ranking') {
      checkChoiceIndexes(tag, item.ans, bound, false, violations)
    } else if (type === 'matrix') {
      if (!item.ans.every((row) => Array.isArray(row))) {
        violations.push(`${tag}: 矩陣題 ans 應為二維陣列`)
      } else {
        const rowCount = Array.isArray(question.rows) ? question.rows.length : 0
        if (rowCount > 0 && item.ans.length !== rowCount) {
          violations.push(
            `${tag}: 矩陣題 ans 列數 ${item.ans.length} 與題目列數 ${rowCount} 不符（隱藏列應補 []）`
          )
        }
      }
    } else if (type === 'nouislider') {
      // 尺規(5)/比較(8)：全為數值
      if (item.ans.some((v) => typeof v !== 'number' || Number.isNaN(v))) {
        violations.push(`${tag}: 尺規/比較題 ans 含非數值 ${JSON.stringify(item.ans)}`)
      }
    } else if (OPEN_TYPES.includes(type)) {
      // 開放/簽名：字串或數字（無值為 ""，不可 null）
      if (
        item.ans.some((v) => typeof v !== 'string' && typeof v !== 'number')
      ) {
        violations.push(`${tag}: 開放題 ans 應為字串/數字 ${JSON.stringify(item.ans)}`)
      }
    }

    // ord 檢查（有選項的題才檢）
    if (Array.isArray(item.ord) && ordLen > 0) {
      if (item.ord.some((v) => !isInt(v))) {
        violations.push(`${tag}: ord 含非整數 ${JSON.stringify(item.ord)}`)
      } else if (
        hasDuplicates(item.ord) ||
        item.ord.some((v) => v < 0 || v >= ordLen)
      ) {
        // 「排列不變量」目前不進 Discord：useConvertSurvey.js 的 convertChoices
        // 在 filter 掉 o_type 6（其他）之前就指派 originalIndex，造成編號空洞，
        // 而 ord 送的正是 originalIndex，所以「其他」不在選項最後的既有問卷
        // 會系統性違規（例：P240304111433）。這是真 bug，但用 Discord 逐人通報
        // 等於洗版。等 convertChoices 修好再把這條升回 violations。
        console.warn(
          `[answerFormat] ${tag}: ord 非合法排列 ${JSON.stringify(item.ord)}`
        )
      }
    }

    // other 的 key 與組裝端 otherIndexOf 相同；條件隱藏不會縮短 choices。
    // 單選僅接受完整索引；複選另接受顯示位置，避免單選 none 與顯示 other 撞號。
    // 僅列入既有 fail-open 通報，不阻擋暫存或交卷。
    const visibleOtherIndex = question.showOtherItem
      ? question.visibleChoices?.findIndex(
          (choice) => choice.value === question.otherItem?.value
        )
      : -1
    const otherSelected = otherIndex >= 0 && (
      (isMultiple
        ? visibleOtherIndex >= 0 && item.ans.includes(visibleOtherIndex)
        : item.ans.includes(otherIndex))
    )
    if (otherSelected &&
      (typeof item.other?.[otherIndex] !== 'string' || !item.other[otherIndex].trim())) {
      violations.push(`${tag}: ans 選了其他但 other[${otherIndex}] 文字缺漏`)
    }

    // other 的完整選項 key 不受可見選項數限制，其餘 key 沿用範圍檢查。
    if (item.other && typeof item.other === 'object' && !Array.isArray(item.other)) {
      Object.entries(item.other).forEach(([k, v]) => {
        const idx = Number(k)
        if (!isInt(idx) || idx < 0 || (bound > 0 && idx >= bound && idx !== otherIndex)) {
          violations.push(`${tag}: other 的 key ${k} 不是有效選項索引`)
        }
        if (typeof v !== 'string') {
          violations.push(`${tag}: other[${k}] 內容不是字串`)
        }
      })
    }

    // bringing_options 內容檢查
    //
    // 這裡「不」檢查帶入題是否漏了 bringing_options：判定帶入題需要
    // choiceIndexMapRef（組裝端用 choiceIndexMapRef[key][0].title 判定），
    // 而驗證器只拿得到 SurveyJS model。從 model 走 question.choices[0].origin_op
    // 是無效的——origin_op / origin_qi 不是註冊過的 ItemValue 屬性，
    // survey-core 在 JsonObject.toObjectCore 會直接丟棄，該判斷恆為 false。
    // 要補這條就得把 choiceIndexMapRef 一起傳進來，另案處理。
    if (item.bringing_options) {
      const opts = item.bringing_options.options
      if (!Array.isArray(opts) || opts.length === 0) {
        violations.push(`${tag}: bringing_options.options 缺少或為空`)
      } else {
        // API 規格：options 長度需 ≥ 答案數；帶入題的 ans 是 options 內的位置
        if (opts.length < item.ans.length) {
          violations.push(
            `${tag}: bringing_options.options 只有 ${opts.length} 個，少於答案數 ${item.ans.length}`
          )
        }
        item.ans.forEach((v) => {
          // 一般選項範圍檢查已報過同一索引就不重複（兩者通常同時成立）
          const reported = violations.some((x) =>
            x.startsWith(`${tag}: ans 索引 ${v} 超出選項範圍`)
          )
          if (isInt(v) && v >= opts.length && !reported) {
            violations.push(
              `${tag}: ans 索引 ${v} 超出帶入選項範圍（共 ${
                opts.length
              } 個帶入選項，應為 0～${opts.length - 1}）`
            )
          }
        })
        opts.forEach((o, i) => {
          if (!o || typeof o.na !== 'string' || o.na === '') {
            violations.push(`${tag}: bringing_options.options[${i}] 缺 na`)
          }
          if (o && !isInt(o.origin_qi)) {
            // 例：暫存路徑曾誤送字串 "q3"
            violations.push(
              `${tag}: bringing_options.options[${i}] origin_qi 不是整數（實際值 ${JSON.stringify(
                o.origin_qi
              )}）`
            )
          }
          if (o && o.origin_op === undefined) {
            violations.push(`${tag}: bringing_options.options[${i}] 缺 origin_op`)
          } else if (o && !isInt(o.origin_op)) {
            // 例：來源題勾「其他」時曾誤送字串 'other'，應為 op 中 o_type 6 的索引
            violations.push(
              `${tag}: bringing_options.options[${i}] origin_op 不是整數（實際值 ${JSON.stringify(
                o.origin_op
              )}，選項「${o.na}」）`
            )
          }
        })
      }
    }
  })

  return violations
}

// 組 Discord 通報訊息。webhook 上限 2000 字，單筆違規含 JSON.stringify 可能很長，
// 超長會回 400 讓整則通報靜默消失，所以條數與總長度都要截。
// 依前端網域判斷站台，方便分辨通報來自正式站還是測試站
function getSiteLabel() {
  const hostname = window.location.hostname
  if (hostname === 'ezcweb.dsigroup.com.tw') return `正式站 (${hostname})`
  if (hostname === 'devwebbj.dsigroup.com.tw') return `測試站 (${hostname})`
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `本機 (${hostname})`
  }
  return `未知站台 (${hostname})`
}

// 診斷用的短預覽：先截長字串再序列化，避免檔案題 {content}／簽名題 data URL
// 這類 base64 整段展開；序列化失敗也只影響該欄，違規清單照常送出
const PREVIEW_LEN = 80
function preview(value) {
  try {
    const json = JSON.stringify(value, (_, v) =>
      typeof v === 'string' && v.length > PREVIEW_LEN
        ? `${v.slice(0, PREVIEW_LEN)}…(len=${v.length})`
        : v
    )
    return json === undefined ? 'undefined' : json.slice(0, PREVIEW_LEN)
  } catch (e) {
    return '(無法序列化)'
  }
}

// 開放題可能含姓名、電話等個資，診斷只送型別與長度，不送原文
const OPEN_TEXT_TYPES = ['text', 'comment', 'multipletext']
function maskedPreview(value) {
  if (typeof value === 'string') return `string(len=${value.length})`
  if (Array.isArray(value)) {
    return `array(${value.map((v) => maskedPreview(v)).join(',')})`.slice(
      0,
      PREVIEW_LEN
    )
  }
  if (value && typeof value === 'object') {
    return `object(keys=${Object.keys(value).length})`
  }
  return String(value)
}

// 通報用的 answers 副本：開放題的 ans 與所有題目的 other（「其他」說明文字）
// 改成 <string len=N>，其餘欄位不動，方便對照格式又不外流個資 [9EZ-577]
export function maskOpenTextAnswers(survey, answers) {
  if (!Array.isArray(answers)) return answers
  const mask = (v) => (typeof v === 'string' ? `<string len=${v.length}>` : v)
  return answers.map((item) => {
    if (!item || typeof item !== 'object') return item
    const isOpenText = OPEN_TEXT_TYPES.includes(
      survey?.getQuestionByName?.(`q${item.qi}`)?.getType?.()
    )
    const other =
      item.other && typeof item.other === 'object'
        ? Object.fromEntries(
            Object.entries(item.other).map(([k, v]) => [k, mask(v)])
          )
        : item.other
    return {
      ...item,
      ans:
        isOpenText && Array.isArray(item.ans) ? item.ans.map(mask) : item.ans,
      other,
    }
  })
}

// 依違規訊息裡的 (qi=N) 找出出事的題目，附上該題的原始值、送出的 ans、
// choiceIndexMapRef 的 key（帶入題/選項對應是否建好）與 matrixAnswers 狀態，追查時用
export function buildViolationDiagnostics(
  survey,
  answers,
  violations,
  { choiceIndexMap, matrixAnswers } = {}
) {
  const qis = [
    ...new Set(
      violations
        .map((v) => v.match(/\(qi=(\d+)\)/)?.[1])
        .filter((v) => v !== undefined)
        .map(Number)
    ),
  ]
  return qis.slice(0, 5).map((qi) => {
    // 題名是 q + qi（查表用）；顯示用與違規列相同的 tag（qi + 1），方便對照
    const name = `q${qi}`
    const tag = `q${qi + 1}(qi=${qi})`
    const item = Array.isArray(answers)
      ? answers.find((a) => a?.qi === qi)
      : undefined
    const raw = survey?.getValue?.(name)
    const isOpenText = OPEN_TEXT_TYPES.includes(
      survey?.getQuestionByName?.(name)?.getType?.()
    )
    const show = isOpenText ? maskedPreview : preview
    const parts = [
      `${tag}: typeof=${raw === null ? 'null' : typeof raw}`,
      `data=${show(raw)}`,
      `ans=${show(item?.ans)}`,
      `bringing_options=${item?.bringing_options ? '有' : '無'}`,
    ]
    if (choiceIndexMap) {
      parts.push(
        `choiceIndexMap keys=[${Object.keys(choiceIndexMap[name] || {})
          .join(',')
          .slice(0, PREVIEW_LEN)}]`
      )
    }
    if (matrixAnswers) {
      parts.push(`matrixAnswers有此題=${name in matrixAnswers}`)
    }
    return parts.join(' ')
  })
}

// diagnostics：附加診斷行（buildViolationDiagnostics 產生），不計入違規條數
// head 各欄先截長度，body 預算至少留 0，避免 slice 負數從尾端回切
export function formatViolationMessage(
  pid,
  violations,
  source,
  { ac, title, diagnostics = [] } = {}
) {
  const cut = (text, len) =>
    text.length > len ? `${text.slice(0, len)}…` : text
  const head =
    `⚠️ EZC Web 答案格式檢核異常（${source}） pid: ${pid}\n` +
    `問卷: ${cut(String(title || '(未取得)'), 100)}\n` +
    `站台: ${getSiteLabel()}\n` +
    `ac: ${cut(String(ac ?? '(無)'), 60)}\n` +
    `UA: ${cut(navigator.userAgent || '', 200)}\n` +
    (diagnostics.length > 0
      ? `診斷:\n${cut(diagnostics.join('\n'), 600)}\n`
      : '') +
    '---\n'
  const tail = violations.length > 15 ? `\n...等共 ${violations.length} 項` : ''
  const body = violations.slice(0, 15).join('\n')
  const room = Math.max(0, 1800 - head.length - tail.length)
  return (
    head +
    (body.length > room ? `${body.slice(0, room)}…（已截斷）` : body) +
    tail
  )
}
